import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Edit2, Plus, Save, Search, Trash2, X } from "lucide-react";
import PageContainer from "../../components/PageContainer";
import {
  fornecedorService,
  pedidoCompraService,
  produtoService,
  unidadeControleService,
} from "../../services/apiService";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { useNotification } from "../../hooks/useNotification";
import { ToggleSwitch } from "../../components/ui";

const vazio = {
  descricao: "",
  ean: "",
  unidadeControleId: "",
  fornecedores: [],
  ativo: true,
};
export default function Produto() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [retornoLista] = useState(() => ({
    ativo: params.get("retornoLista") === "1",
    pedidoId: params.get("pedidoId"),
    itemId: params.get("itemId"),
    expectedVersion: params.get("expectedVersion"),
  }));
  const [busca, setBusca] = useState(""),
    [situacao, setSituacao] = useState("TODOS"),
    [form, setForm] = useState(vazio),
    [edit, setEdit] = useState(null),
    [aberto, setAberto] = useState(false),
    [loading, setLoading] = useState(false),
    [similaridade, setSimilaridade] = useState(null);
  const [pagina, setPagina] = useState(0);
  const tamanhoPagina = 50;
  const { showSuccess, showError } = useNotification();
  const confirmar = useConfirmDialog();
  const produtosQuery = useQuery({
    queryKey: ["produtos-cadastro", busca, situacao, pagina],
    queryFn: async () => {
      try {
        return await produtoService.listarPagina({
          busca,
          situacao,
          pagina,
          tamanho: tamanhoPagina,
        });
      } catch (e) {
        showError(e.message || "Não foi possível carregar produtos.");
        throw e;
      }
    },
    staleTime: 10000,
    retry: 0,
  });
  const unidadesQuery = useQuery({
    queryKey: ["unidades-controle-ativas"],
    queryFn: () => unidadeControleService.listar({ situacao: "ATIVO" }),
    staleTime: 60000,
    retry: 0,
  });
  const fornecedoresQuery = useQuery({
    queryKey: ["fornecedores-ativos"],
    queryFn: () => fornecedorService.listar({ situacao: "ATIVO" }),
    staleTime: 60000,
    retry: 0,
  });
  const items = produtosQuery.data?.itens || [];
  const temProximaPagina = Boolean(produtosQuery.data?.temProxima);

  useEffect(() => {
    if (produtosQuery.isSuccess && !items.length && pagina > 0) {
      setPagina((atual) => Math.max(0, atual - 1));
    }
  }, [produtosQuery.isSuccess, items.length, pagina]);
  const unidades = unidadesQuery.data || [];
  const fornecedores = fornecedoresQuery.data || [];
  const carregandoDados =
    loading ||
    produtosQuery.isLoading ||
    unidadesQuery.isLoading ||
    fornecedoresQuery.isLoading;
  useEffect(() => {
    if (params.get("novo") === "1") {
      setEdit(null);
      setForm({ ...vazio, descricao: params.get("descricao") || "" });
      setAberto(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  const cancelar = (limparAviso = true) => {
    setEdit(null);
    setForm(vazio);
    if (limparAviso) setSimilaridade(null);
    setAberto(false);
  };
  const alterarFornecedor = (i, campo, valor) =>
    setForm((f) => ({
      ...f,
      fornecedores: f.fornecedores.map((x, n) =>
        n === i ? { ...x, [campo]: valor } : x,
      ),
    }));
  const salvar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let alerta = null;
      if (!edit && form.descricao && form.unidadeControleId) {
        // Falha na consulta interrompe o cadastro (cai no catch abaixo e mostra o erro).
        alerta = await produtoService.similaridade({
          descricao: form.descricao,
          unidadeControleId: form.unidadeControleId,
          ean: form.ean,
        });
        if (alerta.similar) setSimilaridade(alerta);
      }
      const salvo = edit
        ? await produtoService.atualizar(edit.id, form)
        : await produtoService.criar(form);
      if (edit && form.ativo !== edit.ativo)
        await produtoService.alterarSituacao(edit.id, form.ativo);
      if (!edit && retornoLista.ativo) {
        await pedidoCompraService.converterItemLivreEmNovoProduto(
          retornoLista.pedidoId,
          retornoLista.itemId,
          {
            produtoId: salvo.id,
            expectedVersion: Number(retornoLista.expectedVersion),
          },
        );
        showSuccess("Produto cadastrado e item da lista atualizado.");
        navigate("/lista-compras");
        return;
      }
      showSuccess(
        edit
          ? "Produto atualizado com sucesso."
          : "Produto cadastrado com sucesso.",
      );
      if (alerta?.similar) setSimilaridade(alerta);
      cancelar(false);
      await produtosQuery.refetch();
    } catch (err) {
      showError(err.message || "Não foi possível salvar o produto.");
    } finally {
      setLoading(false);
    }
  };
  const excluir = async (p) => {
    if (
      !(await confirmar({
        title: "Excluir produto",
        description: `Deseja excluir ${p.descricao}? O produto ficará oculto.`,
      }))
    )
      return;
    setLoading(true);
    try {
      await produtoService.excluir(p.id);
      showSuccess("Produto excluído com sucesso.");
      await produtosQuery.refetch();
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <PageContainer className="estoque-cadastro p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Produtos</h1>
        <p className="text-gray-600">
          Cadastre produtos e seus códigos por fornecedor.
        </p>
      </div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row">
          <label className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              className="w-full rounded-md border py-2 pl-10 pr-3"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setPagina(0);
              }}
              placeholder="Buscar por código ou descrição..."
            />
          </label>
          <select
            className="rounded-md border px-3 py-2"
            value={situacao}
            onChange={(e) => {
              setSituacao(e.target.value);
              setPagina(0);
            }}
          >
            <option value="TODOS">Todos</option>
            <option value="ATIVO">Ativos</option>
            <option value="INATIVO">Inativos</option>
          </select>
        </div>
        <button
          className="rounded bg-blue-600 px-4 py-2 text-white"
          onClick={() => {
            setEdit(null);
            setForm(vazio);
            setSimilaridade(null);
            setAberto(true);
          }}
        >
          <Plus size={18} className="inline" /> Novo produto
        </button>
      </div>
      {similaridade?.similar && !aberto && (
        <div className="mb-6 rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <strong>Produto salvo com possíveis semelhantes.</strong>
              <ul className="mt-1 list-disc pl-5">
                {similaridade.candidatos.map((x) => (
                  <li key={x.produtoId}>
                    {x.descricao} — {x.confianca}% ({x.motivo})
                  </li>
                ))}
              </ul>
              <span className="mt-1 block">
                O cadastro não foi bloqueado. Revise os candidatos quando
                possível.
              </span>
            </div>
            <button
              type="button"
              className="text-amber-800 underline"
              onClick={() => setSimilaridade(null)}
            >
              Fechar aviso
            </button>
          </div>
        </div>
      )}
      {aberto && (
        <form onSubmit={salvar} className="mb-6 rounded border bg-gray-50 p-5">
          <h2 className="mb-4 text-lg font-semibold">
            {edit ? "Editar produto" : "Novo produto"}
          </h2>
          {similaridade?.similar && (
            <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <strong>Atenção: produto semelhante encontrado.</strong>
              <ul className="list-disc pl-5">
                {similaridade.candidatos.map((x) => (
                  <li key={x.produtoId}>
                    {x.descricao} — {x.confianca}% ({x.motivo})
                  </li>
                ))}
              </ul>
              <span className="block">O cadastro continua permitido.</span>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <Campo
              label="Descrição *"
              value={form.descricao}
              onChange={(v) => {
                setSimilaridade(null);
                setForm({ ...form, descricao: v });
              }}
            />
            <Campo
              label="EAN"
              required={false}
              maxLength={32}
              value={form.ean}
              onChange={(v) => {
                setSimilaridade(null);
                setForm({ ...form, ean: v });
              }}
            />
            <label>
              Unidade de controle *
              <select
                required
                className="mt-1 w-full rounded border p-2"
                value={form.unidadeControleId}
                onChange={(e) => {
                  setSimilaridade(null);
                  setForm({ ...form, unidadeControleId: e.target.value });
                }}
              >
                <option value="">Selecione</option>
                {unidades.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome} ({u.simbolo})
                  </option>
                ))}
              </select>
            </label>
            {edit && (
              <label className="flex items-center justify-between rounded border bg-white px-4 py-3">
                <span>Status do produto</span>
                <ToggleSwitch
                  checked={form.ativo}
                  onChange={(e) =>
                    setForm({ ...form, ativo: e.target.checked })
                  }
                  label="Ativo"
                  aria-label="Produto ativo"
                />
              </label>
            )}
          </div>
          <div className="mt-5 rounded border bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold">Fornecedores</h3>
                <p className="text-sm text-gray-500">
                  Códigos usados pelo fornecedor.
                </p>
              </div>
              <button
                type="button"
                className="rounded border px-3 py-2 text-sm"
                onClick={() =>
                  setForm({
                    ...form,
                    fornecedores: [
                      ...form.fornecedores,
                      { fornecedorId: "", codigo: "", principal: false },
                    ],
                  })
                }
              >
                <Plus size={16} className="inline" /> Adicionar
              </button>
            </div>
            {form.fornecedores.map((f, i) => (
              <div
                className="mt-3 grid gap-3 rounded border bg-gray-50 p-3 md:grid-cols-[1fr_1fr_auto_auto]"
                key={i}
              >
                <label>
                  Fornecedor
                  <select
                    required
                    value={f.fornecedorId}
                    className="mt-1 w-full rounded border p-2"
                    onChange={(e) =>
                      alterarFornecedor(i, "fornecedorId", e.target.value)
                    }
                  >
                    <option value="">Selecione</option>
                    {fornecedores.map((x) => (
                      <option value={x.id} key={x.id}>
                        {x.razaoSocial}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Código
                  <input
                    required
                    className="mt-1 w-full rounded border p-2"
                    value={f.codigo}
                    onChange={(e) =>
                      alterarFornecedor(i, "codigo", e.target.value)
                    }
                  />
                </label>
                <ToggleSwitch
                  checked={f.principal}
                  onChange={() =>
                    setForm({
                      ...form,
                      fornecedores: form.fornecedores.map((x, n) => ({
                        ...x,
                        principal: n === i ? !f.principal : false,
                      })),
                    })
                  }
                  label="Principal"
                  aria-label={`Fornecedor ${i + 1} principal`}
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      fornecedores: form.fornecedores.filter((_, n) => n !== i),
                    })
                  }
                  className="text-red-600"
                >
                  Remover
                </button>
              </div>
            ))}
          </div>
          <div className="mt-5 flex gap-2">
            <button className="rounded bg-green-600 px-4 py-2 text-white">
              <Save size={16} className="inline" /> Salvar
            </button>
            <button
              type="button"
              onClick={cancelar}
              className="rounded bg-gray-500 px-4 py-2 text-white"
            >
              <X size={16} className="inline" /> Cancelar
            </button>
          </div>
        </form>
      )}
      <div className="overflow-x-auto rounded border bg-white">
        <table className="estoque-table w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3">Código</th>
              <th>Descrição</th>
              <th>Unidade</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr className="border-t" key={p.id}>
                <td className="p-3">{p.codigo}</td>
                <td>{p.descricao}</td>
                <td>{p.unidadeControleSimbolo || "—"}</td>
                <td>{p.ativo ? "Ativo" : "Inativo"}</td>
                <td className="p-3">
                  <button
                    onClick={() => {
                      setEdit(p);
                      setSimilaridade(null);
                      setForm({
                        descricao: p.descricao,
                        ean: p.ean || "",
                        unidadeControleId: p.unidadeControleId,
                        fornecedores: p.fornecedores.map((x) => ({
                          fornecedorId: x.fornecedorId,
                          codigo: x.codigo,
                          principal: x.principal,
                        })),
                        ativo: p.ativo,
                      });
                      setAberto(true);
                    }}
                    className="mr-2 text-blue-600"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => excluir(p)} className="text-red-600">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {!carregandoDados && items.length === 0 && (
              <tr>
                <td colSpan="5" className="p-8 text-center text-gray-500">
                  Nenhum produto encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center justify-between rounded border bg-white px-4 py-3 text-sm">
        <button
          type="button"
          className="rounded border px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={pagina === 0 || carregandoDados}
          onClick={() => setPagina((atual) => Math.max(0, atual - 1))}
        >
          Anterior
        </button>
        <span className="text-gray-600">Página {pagina + 1}</span>
        <button
          type="button"
          className="rounded border px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!temProximaPagina || carregandoDados}
          onClick={() => setPagina((atual) => atual + 1)}
        >
          Próxima
        </button>
      </div>
    </PageContainer>
  );
}
function Campo({ label, value, onChange, required = true, maxLength = 220 }) {
  return (
    <label>
      {label}
      <input
        required={required}
        maxLength={maxLength}
        className="mt-1 w-full rounded border p-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
